import { BufferAttribute, BufferGeometry, Mesh } from 'three'
import type { MachineDefinition, MachineDevice } from '@core/machine'
import type { BuiltDevice } from './deviceMeshes'
import { type Finish, type PartPalette } from './partsKit'
import type asset from '../../SourceArt/PierLowerAssembly/mesh.json'

let data: typeof asset | undefined
export async function prepareAuthoredLowerAssembly(): Promise<void> {
  data ??= (await import('../../SourceArt/PierLowerAssembly/mesh.json')).default
}
function instanceFor(definition: MachineDefinition, id: string) {
  const instances = data?.machines[definition.machineId as keyof typeof asset.machines]
  const instance = instances?.find(item => item.id === id)
  const device = definition.devices.find(item => item.id === id)
  const body = definition.colliders.find(item => item.id === instance?.bodyId)
  if (instance === undefined || device?.kind !== 'slingshot' || body?.kind !== 'polyline'
    || body.material !== 'plastic' || body.layer !== 'playfield') return undefined
  const same = (a: readonly number[], b: readonly number[]) => a.length === b.length
    && a.every((n, i) => Math.abs(n - b[i]!) <= 1e-9)
  if (!same(device.a, instance.a) || !same(device.b, instance.b) || body.points.length !== instance.bodyPoints.length
    || !body.points.every((point, i) => same(point, instance.bodyPoints[i]!))) return undefined
  return instance
}
export const lowerAssemblyMatches = (definition: MachineDefinition, id: string): boolean => instanceFor(definition, id) !== undefined
export const lowerBodyFits = (definition: MachineDefinition, bodyId: string): boolean =>
  data?.machines[definition.machineId as keyof typeof asset.machines]?.some(instance => instance.bodyId === bodyId
    && instanceFor(definition, instance.id) !== undefined) ?? false
export function withAuthoredLowerAssembly(built: BuiltDevice, device: MachineDevice,
  definition: MachineDefinition, palette: PartPalette, illustrated: boolean): BuiltDevice {
  if (!illustrated || device.kind !== 'slingshot' || built.group === undefined) return built
  const instance = instanceFor(definition, device.id)
  if (instance === undefined) return built
  const geometries: BufferGeometry[] = []
  const meshes: Mesh[] = []
  for (const part of instance.parts) {
    const geometry = new BufferGeometry()
    geometry.setAttribute('position', new BufferAttribute(new Float32Array(part.positions), 3))
    geometry.setAttribute('normal', new BufferAttribute(new Float32Array(part.normals), 3))
    geometry.setIndex(part.triangles); geometry.computeBoundingBox(); geometry.computeBoundingSphere()
    geometries.push(geometry)
    const finish = (part.finish[0]!.toLowerCase() + part.finish.slice(1)) as Finish
    const mesh = new Mesh(geometry, palette.get(finish)); mesh.name = `${device.id}.lower.authored.${part.finish}`
    meshes.push(mesh)
  }
  // The existing moving arm/animator/rebind remains exactly the same object.
  for (const child of [...built.group.children])
    if (child instanceof Mesh && child.name !== `${device.id}.arm`) built.group.remove(child)
  built.group.add(...meshes)
  return { ...built, geometries: [...built.geometries, ...geometries] }
}
